/**
 * Pure batch determination logic: computes dough and oven batches from a requested quantity.
 * Testable without the React context.
 */

export interface DoughBatchInput {
  sequenceNumber: number;
  quantity: number;
  doughQuantityKg: number;
  uuid?: string;
}

export interface DoughBatchResult {
  uuid?: string;
  sequenceNumber: number;
  quantity: number;
  doughQuantityKg: number;
}

export interface OvenBatchResult {
  uuid?: string;
  ovenNumber: number;
  utilization: number;
  pieces: number;
  piecesPerOven: number;
}

export interface MaterialBatchParams {
  piecesPerOven: number;
  initialWeighInGrams: number;
  minDoughQuantityInKg: number;
  maxDoughQuantityInKg: number;
}

export interface BatchDeterminationParams {
  desiredQuantity: number;
  batchDetermination: 'FULL_OVEN' | 'OVEN_UTILIZATION';
  minOvenUtilizationPercent?: number;
  doughBatchMethod: 'even' | 'full';
  material: MaterialBatchParams;
  /** Fixed dough batches that stay unchanged at the start of the result. They must have sequenceNumber 1, 2, 3, ... */
  fixedDoughBatches?: DoughBatchInput[];
}

export interface BatchDeterminationResult {
  doughBatches: DoughBatchResult[];
  ovenBatches: OvenBatchResult[];
  bakedQuantity: number;
  doughQuantity: number;
}

export class FixedDoughBatchesValidationError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'FixedDoughBatchesValidationError';
  }
}

/**
 * Checks whether the fixed dough batches carry the sequence numbers 1, 2, 3, ... without gaps.
 * They have to come first - otherwise something went wrong upstream.
 */
export function validateFixedDoughBatches(fixed: DoughBatchInput[]): void {
  if (!fixed || fixed.length === 0) return;
  const sorted = [...fixed].sort((a, b) => a.sequenceNumber - b.sequenceNumber);
  for (let i = 0; i < sorted.length; i++) {
    if (sorted[i].sequenceNumber !== i + 1) {
      throw new FixedDoughBatchesValidationError(
        `Fixe Teig-Chargen müssen sequenceNumber 1, 2, 3, ... haben. Erwartet ${i + 1}, erhalten ${sorted[i].sequenceNumber}.`
      );
    }
  }
}

/**
 * Determines the effective dough batch method from three sources, in falling priority:
 *   1. the explicitly passed method (e.g. the radio button just clicked)
 *   2. a stored override (a persisted manual choice)
 *   3. the material default (backend enum 'FULL' -> 'full', otherwise 'even')
 *
 * The explicit method MUST take precedence over the override: when the radio buttons are switched,
 * the UI calls setBatchDeterminationOverride(...) and immediately afterwards - in the same event -
 * calculateBatchDetermination(..., method). At that point the override state in the closure is still
 * the OLD value (a React state update is asynchronous). If the override won, the calculation would
 * lag one choice behind: the batches shown would not match the selected radio button.
 */
export function resolveEffectiveDoughBatchMethod(
  passedMethod: 'even' | 'full' | undefined,
  overrideMethod: 'even' | 'full' | undefined,
  materialDoughBatchMethod: string | null | undefined
): 'even' | 'full' {
  const materialDefault = materialDoughBatchMethod === 'FULL' ? 'full' : 'even';
  return passedMethod ?? overrideMethod ?? materialDefault;
}

/**
 * Computes dough and oven batches from a requested quantity (pure logic, no context).
 */
export function calculateBatchDetermination(params: BatchDeterminationParams): BatchDeterminationResult {
  const {
    desiredQuantity,
    batchDetermination,
    minOvenUtilizationPercent = 60,
    doughBatchMethod,
    material,
    fixedDoughBatches
  } = params;

  const { piecesPerOven, initialWeighInGrams, minDoughQuantityInKg, maxDoughQuantityInKg } = material;

  const doughBatches: DoughBatchResult[] = [];

  if (fixedDoughBatches && fixedDoughBatches.length > 0) {
    validateFixedDoughBatches(fixedDoughBatches);
    for (const b of fixedDoughBatches) {
      doughBatches.push({
        uuid: b.uuid,
        sequenceNumber: b.sequenceNumber,
        quantity: b.quantity,
        doughQuantityKg: b.doughQuantityKg
      });
    }
  }

  const fixedPieces = fixedDoughBatches?.reduce((sum, b) => sum + (b.quantity ?? 0), 0) ?? 0;
  const remainingQuantity = Math.max(0, desiredQuantity - fixedPieces);

  if (remainingQuantity <= 0) {
    // Fixed batches only - oven batches from the total piece count
    const totalPieces = fixedPieces;
    const ovenBatches = computeOvenBatches(totalPieces, piecesPerOven, batchDetermination, minOvenUtilizationPercent);
    const totalDoughKg = fixedDoughBatches?.reduce((sum, b) => sum + (b.doughQuantityKg ?? 0), 0) ?? 0;
    return {
      doughBatches,
      ovenBatches,
      bakedQuantity: totalPieces,
      doughQuantity: totalDoughKg
    };
  }

  // Step 2: oven and baking logic for the remaining quantity
  const { bakedQuantity: newBakedQuantity } = computeOvenAndBakedFromQuantity(
    remainingQuantity,
    piecesPerOven,
    batchDetermination,
    minOvenUtilizationPercent
  );

  const totalBakedQuantity = fixedPieces + newBakedQuantity;

  // Recompute the oven batches for the total quantity (not just the remainder - the ovens serve everything)
  const ovenBatchesForTotal = computeOvenBatches(
    totalBakedQuantity,
    piecesPerOven,
    batchDetermination,
    minOvenUtilizationPercent
  );

  // Steps 3 and 4: new dough batches for the remaining quantity
  const doughQuantityForNew = (newBakedQuantity * initialWeighInGrams) / 1000;
  const newDoughBatches = computeDoughBatches(
    doughQuantityForNew,
    newBakedQuantity,
    initialWeighInGrams,
    minDoughQuantityInKg,
    maxDoughQuantityInKg,
    doughBatchMethod,
    fixedDoughBatches?.length ?? 0
  );

  const totalDoughQuantity =
    (fixedDoughBatches?.reduce((s, b) => s + (b.doughQuantityKg ?? 0), 0) ?? 0) + doughQuantityForNew;

  return {
    doughBatches: [...doughBatches, ...newDoughBatches],
    ovenBatches: ovenBatchesForTotal,
    bakedQuantity: totalBakedQuantity,
    doughQuantity: totalDoughQuantity
  };
}

function computeOvenBatches(
  totalPieces: number,
  piecesPerOven: number,
  batchDetermination: 'FULL_OVEN' | 'OVEN_UTILIZATION',
  minOvenUtilizationPercent: number
): OvenBatchResult[] {
  const minUtilization = minOvenUtilizationPercent / 100;
  const totalOvens = totalPieces / piecesPerOven;
  const fullOvens = Math.floor(totalOvens);
  const remainder = totalOvens - fullOvens;

  const ovenBatches: OvenBatchResult[] = [];

  if (batchDetermination === 'FULL_OVEN') {
    const ovenCount = Math.ceil(totalPieces / piecesPerOven);
    for (let i = 0; i < ovenCount; i++) {
      ovenBatches.push({
        uuid: undefined,
        ovenNumber: i + 1,
        utilization: 1.0,
        pieces: piecesPerOven,
        piecesPerOven
      });
    }
  } else if (batchDetermination === 'OVEN_UTILIZATION') {
    // Less than one oven load: one oven batch at at least the minimum utilization (not two half-full ones).
    if (fullOvens === 0 && remainder > 0 && remainder < minUtilization) {
      const minPieces = Math.round(minUtilization * piecesPerOven);
      const pieces = Math.max(totalPieces, minPieces);
      const utilization = pieces / piecesPerOven;
      ovenBatches.push({
        uuid: undefined,
        ovenNumber: 1,
        utilization,
        pieces,
        piecesPerOven
      });
    } else if (remainder > 0 && remainder < minUtilization) {
      // At least one full oven plus a remainder below the minimum utilization: spread the remainder over 2 ovens (the previous logic)
      const actualFullOvens = fullOvens - 1;
      const actualRemainder = remainder + 1.0;
      const perOven = actualRemainder / 2;

      for (let i = 0; i < actualFullOvens; i++) {
        ovenBatches.push({
          uuid: undefined,
          ovenNumber: i + 1,
          utilization: 1.0,
          pieces: piecesPerOven,
          piecesPerOven
        });
      }
      ovenBatches.push({
        uuid: undefined,
        ovenNumber: actualFullOvens + 1,
        utilization: perOven,
        pieces: Math.round(perOven * piecesPerOven),
        piecesPerOven
      });
      ovenBatches.push({
        uuid: undefined,
        ovenNumber: actualFullOvens + 2,
        utilization: perOven,
        pieces: Math.round(perOven * piecesPerOven),
        piecesPerOven
      });
    } else if (remainder >= minUtilization) {
      for (let i = 0; i < fullOvens; i++) {
        ovenBatches.push({
          uuid: undefined,
          ovenNumber: i + 1,
          utilization: 1.0,
          pieces: piecesPerOven,
          piecesPerOven
        });
      }
      ovenBatches.push({
        uuid: undefined,
        ovenNumber: fullOvens + 1,
        utilization: remainder,
        pieces: Math.round(remainder * piecesPerOven),
        piecesPerOven
      });
    } else {
      for (let i = 0; i < fullOvens; i++) {
        ovenBatches.push({
          uuid: undefined,
          ovenNumber: i + 1,
          utilization: 1.0,
          pieces: piecesPerOven,
          piecesPerOven
        });
      }
    }
  }

  return ovenBatches;
}

function computeOvenAndBakedFromQuantity(
  desiredQuantity: number,
  piecesPerOven: number,
  batchDetermination: 'FULL_OVEN' | 'OVEN_UTILIZATION',
  minOvenUtilizationPercent: number
): { bakedQuantity: number; ovenBatches: OvenBatchResult[] } {
  const ovenBatches = computeOvenBatches(
    desiredQuantity,
    piecesPerOven,
    batchDetermination,
    minOvenUtilizationPercent
  );
  const bakedQuantity =
    batchDetermination === 'FULL_OVEN'
      ? Math.ceil(desiredQuantity / piecesPerOven) * piecesPerOven
      : ovenBatches.reduce((sum, ob) => sum + ob.pieces, 0);
  return { bakedQuantity, ovenBatches };
}

function computeDoughBatches(
  doughQuantity: number,
  bakedQuantity: number,
  initialWeighInGrams: number,
  minDoughQuantityInKg: number,
  maxDoughQuantityInKg: number,
  doughBatchMethod: 'even' | 'full',
  startSequenceNumber: number
): DoughBatchResult[] {
  const doughBatches: DoughBatchResult[] = [];

  const buildDoughBatchFromPieces = (sequenceNumber: number, pieces: number) => {
    const weightKg = (pieces * initialWeighInGrams) / 1000;
    doughBatches.push({
      uuid: undefined,
      sequenceNumber,
      quantity: pieces,
      doughQuantityKg: weightKg
    });
  };

  if (doughQuantity <= 0) return doughBatches;

  if (doughBatchMethod === 'full') {
    // Split by pieces: one dough batch can hold at most floor(maxKg*1000/weighIn) pieces,
    // because its total weight has to stay <= maxDoughQuantityInKg.
    const maxPiecesPerBatch = Math.floor((maxDoughQuantityInKg * 1000) / initialWeighInGrams);
    const totalPieces = bakedQuantity;
    const fullBatches = Math.floor(totalPieces / maxPiecesPerBatch);
    const remainderPieces = totalPieces - fullBatches * maxPiecesPerBatch;
    const remainderWeightKg = (remainderPieces * initialWeighInGrams) / 1000;

    if (fullBatches === 0) {
      buildDoughBatchFromPieces(startSequenceNumber + 1, totalPieces);
    } else if (remainderPieces > 0 && remainderWeightKg < minDoughQuantityInKg) {
      // The last batch would fall below the minimum quantity. As in the "full ovens" logic in
      // computeOvenBatches (OVEN_UTILIZATION): merge the last full batch with the remainder and
      // spread it evenly over the last two batches, instead of leaving one batch below the
      // minimum quantity.
      const fullBatchesBeforeSplit = fullBatches - 1;
      const sharedPieces = maxPiecesPerBatch + remainderPieces;
      const firstOfLastTwo = Math.ceil(sharedPieces / 2);
      const secondOfLastTwo = sharedPieces - firstOfLastTwo;

      for (let i = 0; i < fullBatchesBeforeSplit; i++) {
        buildDoughBatchFromPieces(startSequenceNumber + i + 1, maxPiecesPerBatch);
      }
      buildDoughBatchFromPieces(startSequenceNumber + fullBatchesBeforeSplit + 1, firstOfLastTwo);
      buildDoughBatchFromPieces(startSequenceNumber + fullBatchesBeforeSplit + 2, secondOfLastTwo);
    } else {
      for (let i = 0; i < fullBatches; i++) {
        buildDoughBatchFromPieces(startSequenceNumber + i + 1, maxPiecesPerBatch);
      }
      if (remainderPieces > 0) {
        buildDoughBatchFromPieces(startSequenceNumber + fullBatches + 1, remainderPieces);
      }
    }
  } else {
    // Piece-based: choose the number of batches so that no batch exceeds maxDoughQuantityInKg.
    // The weight ratio (doughQuantity / maxKg) would allow a fractional 153.76 pieces per batch
    // and underestimates the count by one when the total weight sits just above a multiple of the
    // maximum quantity -> one batch would get 154 pieces (143.22 kg) and break the 143 kg limit.
    const maxPiecesPerBatch = Math.floor((maxDoughQuantityInKg * 1000) / initialWeighInGrams);
    const total = bakedQuantity;
    const doughBatchCount = maxPiecesPerBatch > 0 ? Math.ceil(total / maxPiecesPerBatch) : 1;
    let assignedSoFar = 0;

    for (let i = 0; i < doughBatchCount; i++) {
      const seq = startSequenceNumber + i + 1;
      let pieces: number;
      if (i === doughBatchCount - 1) {
        pieces = total - assignedSoFar;
      } else {
        const idealCumulative = (i + 1) * (total / doughBatchCount);
        pieces = Math.round(idealCumulative - assignedSoFar);
      }
      assignedSoFar += pieces;
      buildDoughBatchFromPieces(seq, pieces);
    }
  }

  return doughBatches;
}
